// Layer 3 (Transformation) — the Risk route's status read (polish.P20, locked
// comp `risk.html` A): the header's constraint fact, the four-cell band and
// the three tabs' derived headlines. Pure: no React, no engine mutation; the
// session state arrives as an argument.
//
// The band carries every figure the retired overview KPI row printed
// (adjudication 1: registered 9, critical and high by band, loops in manual
// > 4 h), plus the register's largest exposure and control verification
// against its target:
//
//   1. Critical and high — `riskKpis()` critical + high, of `registered`; the
//      comparison names the ids per band from `riskScore()`.
//   2. Largest exposure (per event) — `largestExposure()`, the register's rank-1
//      US$ and what it is measured against.
//   3. Control verification — on-time share of the CV register against
//      `CONTROL_VERIFICATION_TARGET_PCT`; names the late checks.
//   4. Loops in manual > 4 h — `manualLoopBreaches(controlLoops())`, the worst
//      loop's dwell against the rule and the loop it holds.

import type { SessionState } from '@/versions/v1/schema/scenario';
import type { VizTone } from '@/versions/v1/modules/_viz/viz-tone';
import { controlLoops, parseDwellHours } from '@/versions/v1/modules/process/Scene.transform';
import { plantConstraint } from '@/versions/v1/modules/maintenance/Scene.transform';
import { CONTROL_VERIFICATION_TARGET_PCT, RISK_ENTRIES, SAFETY_INTERLOCKS } from './seed';
import { riskKpis } from './RiskKpiRow.transform';
import {
  MANUAL_LOOP_LIMIT_HOURS,
  largestExposure,
  manualLoopBreaches,
  riskAtConstraint,
  riskScore,
  verificationSummary,
  type RiskTab,
  type RiskTextSegment,
} from './Scene.transform';
import { shiftChecklist } from './ShiftChecklist.transform';

export type RiskBandCell = {
  id: 'critical-high' | 'exposure' | 'verification' | 'manual-loops';
  label: string;
  figure: string;
  unit?: string;
  comparison: readonly RiskTextSegment[];
  /** The 3px status rule: colour only for the exception. */
  tone: VizTone;
};

function kpiCount(id: string): number {
  return Number(riskKpis().find((kpi) => kpi.id === id)?.value ?? 0);
}

function idsInBand(band: 'critical' | 'high'): string {
  return RISK_ENTRIES.filter((risk) => riskScore(risk).band === band).map((risk) => risk.id).join(', ');
}

/** A loop's plant name without its tag cross-reference (`Pb rougher cell 1 level`). */
function loopPlace(name: string): string {
  return name.replace(/\s*\(.*\)\s*$/, '');
}

export function riskBand(): readonly RiskBandCell[] {
  const registered = kpiCount('registered');
  const critical = kpiCount('critical');
  const high = kpiCount('high');
  const exposure = largestExposure();
  const cv = verificationSummary();
  const breaches = [...manualLoopBreaches(controlLoops())].sort(
    (a, b) => (parseDwellHours(b.duration) ?? 0) - (parseDwellHours(a.duration) ?? 0),
  );
  const worstLoop = breaches[0];

  const flaggedComparison: RiskTextSegment[] = [];
  if (critical > 0) flaggedComparison.push({ text: `${critical} critical`, strong: true, bad: true }, { text: ` ${idsInBand('critical')}`, nowrap: true });
  if (critical > 0 && high > 0) flaggedComparison.push({ text: ' · ' });
  if (high > 0) flaggedComparison.push({ text: `${high} high`, strong: true }, { text: ` ${idsInBand('high')}`, nowrap: true });
  if (critical === 0 && high === 0) flaggedComparison.push({ text: '0 critical · 0 high' });

  return [
    {
      id: 'critical-high',
      label: 'Critical and high',
      figure: String(critical + high),
      unit: `of ${registered} registered`,
      comparison: flaggedComparison,
      tone: critical > 0 ? 'bad' : high > 0 ? 'warn' : 'neutral',
    },
    {
      id: 'exposure',
      label: 'Largest exposure',
      figure: exposure?.dollars ?? '0',
      unit: 'per event',
      comparison: exposure?.comparison ?? [],
      tone: 'neutral',
    },
    {
      id: 'verification',
      label: 'Control verification',
      figure: `${cv.onTimePct}%`,
      unit: 'on time',
      comparison: [
        { text: `${cv.onTimeCount} of ${cv.totalChecks}`, strong: true },
        { text: ` vs the ${CONTROL_VERIFICATION_TARGET_PCT}% target` },
        ...(cv.lateIds.length > 0 ? [{ text: ` · ${cv.lateIds.join(', ')} late` }] : []),
      ],
      tone: cv.onTimePct >= CONTROL_VERIFICATION_TARGET_PCT ? 'neutral' : 'warn',
    },
    {
      id: 'manual-loops',
      label: `Loops in manual > ${MANUAL_LOOP_LIMIT_HOURS} h`,
      figure: String(breaches.length),
      comparison: worstLoop
        ? [
            { text: worstLoop.tag, strong: true },
            { text: ` ${worstLoop.duration} vs the ${MANUAL_LOOP_LIMIT_HOURS} h rule · ` },
            { text: loopPlace(worstLoop.name), nowrap: true },
          ]
        : [{ text: `every loop in AUTO or under the ${MANUAL_LOOP_LIMIT_HOURS} h rule` }],
      tone: breaches.length > 0 ? 'warn' : 'neutral',
    },
  ];
}

/** The header fact: the plant's constraint as the Capacity engine reads it
 *  (D27, as on Maintenance), and how many registered risks sit at it. */
export function riskHeaderFact(state: SessionState): { circuit: string; comparison: string } {
  const constraint = plantConstraint(state);
  const atConstraint = RISK_ENTRIES.filter((risk) => riskAtConstraint(risk)).length;
  return {
    circuit: constraint.circuit,
    comparison: `${constraint.comparison} · ${atConstraint} of ${RISK_ENTRIES.length} risks sit at it`,
  };
}

/** Each tab's headline, derived — the figure the tab carries before it opens. */
export function riskTabHeadlines(): Record<RiskTab, string> {
  const atConstraint = RISK_ENTRIES.filter((risk) => riskAtConstraint(risk)).length;
  const checks = shiftChecklist('GM');
  const cv = verificationSummary();
  return {
    'risk-register': `${RISK_ENTRIES.length} risks · ${atConstraint} at the constraint`,
    checks: `${checks.done} of ${checks.total} done · ${checks.atRisk} at risk`,
    controls: `${SAFETY_INTERLOCKS.length} interlocks · ${cv.degradedResults} degraded`,
  };
}
